import { AfterViewInit, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { NationaalGeoregisterRevGeo } from '../../../../app/interfaces/external-services/nationaal-georegister-rev-geo';
import { NationaalGeoregisterLookup } from '../../../../app/interfaces/external-services/nationaal-georegister-lookup';
import { FormBuilder, FormControl } from '@angular/forms';
import { SnackbarService } from '../../../../app/services/base/snackbar.service';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { NationaalGeoregisterSuggest } from '../../../../app/interfaces/external-services/nationaal-georegister-suggest';
import { Subject } from 'rxjs';
import { SearchAddressResults } from '../../../../app/interfaces/base/search-address-results';
import { LeafletMapService } from '../../../../app/services/base/leaflet-map.service';
import { AuthenticationService } from '../../../../app/services/base/authentication.service';
import { Organisation } from '../../../../app/interfaces/routering/organisation';

@Component({
  selector: 'fury-location-selector-dialog',
  templateUrl: './location-selector-dialog.component.html',
  styleUrls: ['./location-selector-dialog.component.scss'],
})

export class LocationSelectorDialogComponent implements OnInit, AfterViewInit, OnDestroy {

  public isLoading: boolean = false;
  public showClear: boolean = false;

  public form = this.fb.group({
    address: new FormControl({}, {nonNullable: true}),
    lat: null,
    lng: null,
    new_rd_lat: null,
    new_rd_lng: null,
    streetname: null,
    house_number: null,
    house_number_addition: null,
    postalcode: null,
    city: null,
  });

  public disableSave: boolean = false;

  public searchSuggestions: SearchAddressResults[] = null;

  private destroy$: Subject<null> = new Subject<null>();

  constructor(@Inject(MAT_DIALOG_DATA) public data: any,
              private http: HttpClient,
              private fb: FormBuilder,
              private snackbarService: SnackbarService,
              private authenticationService: AuthenticationService,
              public leafletMapService: LeafletMapService,
  ) {
  }

  ngOnInit(): void {
    this.form.controls.lat.setValue(this.data.lat.value);
    this.form.controls.lng.setValue(this.data.lng.value);
    this.form.controls.new_rd_lat.setValue(this.data.new_rd_lat.value);
    this.form.controls.new_rd_lng.setValue(this.data.new_rd_lng.value);
    this.form.controls.streetname.setValue(this.data.streetname.value);
    this.form.controls.house_number.setValue(this.data.house_number.value);
    this.form.controls.house_number_addition.setValue(this.data.house_number_addition.value);
    this.form.controls.postalcode.setValue(this.data.postalcode.value);
    this.form.controls.city.setValue(this.data.city.value);
  }

  ngAfterViewInit(): void {
    if (typeof this.data.lat.value !== 'undefined' && typeof this.data.lng.value !== 'undefined' && this.data.lat.value !== null && this.data.lng.value !== null) {
      this.leafletMapService.updateMarker([this.data.lat.value, this.data.lng.value]);
    }
    if (typeof this.data.streetname.value !== 'undefined' && this.data.streetname.value !== null) {
      this.form.controls.address.setValue({
        weergavenaam: this.data.streetname.value + ' ' + this.data.house_number.value + (this.data.house_number_addition.value ?? '') + ', ' + this.data.city.value
      });
    }

    this.form.controls.address.valueChanges
      .pipe(
        debounceTime(250),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(value => {
        this.showClear = true;
        if (typeof value === 'string') {
          this.doSuggestQuery(value);
        } else {
          this.doLookupRequest((<any>value).id, true);
        }
      });
  }

  clear(): void {
    this.form.reset();
    this.showClear = false;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    this.leafletMapService.clear();
  }

  onMapClick(event: any): void {
    if (!this.disableSave) {
      this.doReverseSearch([event.latlng.lat, event.latlng.lng]);
    }
  }

  showSelectedResult(result: any): string {
    return result && result.weergavenaam ? result.weergavenaam : '';
  }

  doReverseSearch(coords: [number, number]): void {
    const url: string = 'https://api.pdok.nl/bzk/locatieserver/search/v3_1/reverse' +
      '?&lat=' + encodeURIComponent(coords[0]) +
      '&lon=' + encodeURIComponent(coords[1]) +
      '&rows=1&type=adres';

    this.http.get(url, {
      observe: 'response',
      responseType: 'json'
    }).subscribe(
      (response: HttpResponse<NationaalGeoregisterRevGeo>): void => {
        if (response.body.response.numFound > 0) {
          this.doLookupRequest(response.body.response.docs[0].id);

          this.form.controls.lat.setValue(coords[0]);
          this.form.controls.lng.setValue(coords[1]);
          const newRd = this.leafletMapService.convertLatLngToNewRd([coords[1], coords[0]]);
          this.form.controls.new_rd_lat.setValue(newRd[0].toFixed(3));
          this.form.controls.new_rd_lng.setValue(newRd[1].toFixed(3));

          if (typeof this.data.geocoding !== 'undefined' && this.data.geocoding.value) {
            this.form.controls.address.setValue(
              {
                weergavenaam: response.body.response.docs[0].weergavenaam
              },
              {
                emitEvent: false
              }
            );
          }

          this.leafletMapService.updateMarker(coords);
        }
      },
      (): void => {
        this.snackbarService.warning('Er is een probleem opgetreden met de externe service. Probeer het opnieuw.');
      }
    );
  }

  doSuggestQuery(value: string): void {
    this.isLoading = true;
    this.authenticationService.organisation$.pipe(takeUntil(this.destroy$)).subscribe((organisation: Organisation): void => {
      const url: string = 'https://api.pdok.nl/bzk/locatieserver/search/v3_1/suggest' +
        '?q=' + encodeURIComponent(value) +
        '&rows=10' +
        '&bq=' + encodeURIComponent('type:provincie^0.5') +
        '&bq=' + encodeURIComponent('type:gemeente^0.5') +
        '&bq=' + encodeURIComponent('type:woonplaats^0.5') +
        '&bq=' + encodeURIComponent('type:weg^0.5') +
        '&bq=' + encodeURIComponent('type:adres^1.5') +
        '&fq=bron:"BAG"' +
        (organisation ?
          '&fq=gemeentenaam:(' + organisation.center_municipality + ')'
          : '');

      this.http.get(url, {
        observe: 'response',
        responseType: 'json'
      }).subscribe(
        (response: HttpResponse<NationaalGeoregisterSuggest>): void => {
          if (response.body.response.numFound > 0) {
            this.searchSuggestions = response.body.response.docs;
          } else {
            this.searchSuggestions = [{
              id: null,
              weergavenaam: 'Er zijn geen resultaten gevonden, probeer een ander adres.'
            }];
          }
          this.isLoading = false;
        },
        (): void => {
          this.snackbarService.warning('Er is een probleem opgetreden met de externe service. Probeer het opnieuw.');
          this.isLoading = false;
        });
    });
  }

  doLookupRequest(id: string, moveMarker: boolean = false): void {
    this.isLoading = true;
    const url: string = 'https://api.pdok.nl/bzk/locatieserver/search/v3_1/lookup' +
      '?id=' + encodeURIComponent(id);

    this.http.get(url, {
      observe: 'response',
      responseType: 'json'
    }).subscribe(
      (response: HttpResponse<NationaalGeoregisterLookup>): void => {
        if (response.body.response.numFound > 0) {
          const data = response.body.response.docs[0],
            dataLatLng: string[] = data.centroide_ll.replace('POINT(', '').replace(')', '').split(' '),
            dataNewRd: string[] = data.centroide_rd.replace('POINT(', '').replace(')', '').split(' ');

          if (moveMarker) {
            this.form.controls.lat.setValue(parseFloat(dataLatLng[1]));
            this.form.controls.lng.setValue(parseFloat(dataLatLng[0]));
            this.form.controls.new_rd_lat.setValue(dataNewRd[0]);
            this.form.controls.new_rd_lng.setValue(dataNewRd[1]);
            this.leafletMapService.updateMarker([parseFloat(dataLatLng[1]), parseFloat(dataLatLng[0])]);
          }
          this.form.controls.streetname.setValue(data.straatnaam ?? null);
          this.form.controls.house_number.setValue(data.huisnummer ?? null);
          this.form.controls.house_number_addition.setValue(data.huisletter ?? null);
          this.form.controls.postalcode.setValue(data.postcode ?? null);
          this.form.controls.city.setValue(data.woonplaatsnaam ?? null);

          this.form.controls.address.setValue(
            {
              weergavenaam: data.straatnaam + ' ' + data.huis_nlt + ', ' + data.woonplaatsnaam
            },
            {
              emitEvent: false
            }
          );
        }
        this.isLoading = false;
      },
      (): void => {
        this.snackbarService.warning('Er is een probleem opgetreden met de externe service. Probeer het opnieuw.');
        this.isLoading = false;
      }
    );
  }
}
